import { useState, useEffect, useCallback, useMemo } from "react";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router";
import middleware from "../../../Api/Middleware";
import PageTemplate from "../../../Templates/PageTemplate";
import BrandModals from "../../../Modals/BrandModal";
import TablePreset from "../../../Table/TablePreset";
import { useTable } from "react-table";
import SortableHeader from "../../../Table/TableComponents/SortableHeader";
import CheckboxCell from "../../../Table/TableComponents/CheckboxCell";
import CheckboxHeader from "../../../Table/TableComponents/CheckboxHeader";
import ConfirmationModal from "../../../Modals/GeneralSettings/ConfirmationModal";
import Filters from "../../../Filters/Filters";
import { BackButton } from "../../../HouseStyle/Components/Buttons";
import ToastPromise from "../../../Toasts/ToastPromise";

const BrandSettings = () => {
  const navigate = useNavigate();
  const cookies = new Cookies();
  
  // Filter logic
  const defaultFilters = [
    {
      index: 0,
      order: 1,
      value: 1,
      name: "Status merk",
      field: "status",
      type: "radio",
      options: [
        { name: "Gepubliceerd", value: 1 },
        { name: "Gearchiveerd", value: 0 },
      ],
    },
  ];

  const [tempfilters, setTempFilters] = useState(defaultFilters);
  const [filters, setFilters] = useState(defaultFilters);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [pagination, setPagination] = useState({
    page_size: 10,
    page: 1,
    max_items: 0,
    begin_of_page: 0,
    end_of_page: 0,
  }); // the useState for the pagination for the table
  const [loading, setLoading] = useState(true); // useState for page loading
  const [brands, setBrands] = useState([]); // useState for Brands
  const [show, setShow] = useState(false); // useState for the Loadingscreen
  const [searchengine, setSearchengine] = useState("");
  const [showBrandNew, setShowBrandNew] = useState(false);
  const [showBrandEdit, setShowBrandEdit] = useState(false);
  const [brandEditData, setBrandEditData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [order, setOrder] = useState({ orderBy: '', direction: '' }); // useState for ordering the columns in the table
  const [searchBar, setSearchBar] = useState("");

  // Filter modal close function
  const CloseFilterModal = () => {
    setShowFilterModal(false);
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const searchQuery = searchBar.trim() 
        ? `&searchTerm=${encodeURIComponent(searchBar.trim())}&noSearch=[brand_id,description,keywords,status,sorting_order,updated_at,created_at,updater_user_id,updater_user_id]` 
        : "";
      // Build filter query in the format expected by PHP filtering system
      const filterQuery = filters
        .filter((e) => e.value != null && e.value !== "")
        .map((e) => `${e.field}=${e.value}`)
        .join("|");
      // Add filtering parameter if filters exist
      const filterParam = filterQuery ? `&filtering=${encodeURIComponent(filterQuery)}` : "";
      const [brandsRes] = await Promise.all([
        middleware.get(
          `sp/brandsTableAll?page=${pagination.page}&page_size=${pagination.page_size}${order.orderBy ? `&orderBy=${order.orderBy}` : ""}${order.direction ? `&orderDirection=${order.direction}` : ""}${searchQuery}${filterParam}`
        ),
      ]);
      setBrands(brandsRes.data.data);
      setPagination(prev => ({
        ...prev,
        max_items: brandsRes.data.total,
        begin_of_page: brandsRes.data.from,
        end_of_page: brandsRes.data.to,
      })); // sets the pagination usestate with the max items begin of the page and the end of the page
      setLoadingScreenFunc();
    } catch (error) {
      console.error(error);
      if (error.response?.status === 401) {
        cookies.remove("token", { path: "/" });
        navigate("/");
      }
    }
  };


  useEffect(() => {
    fetchAll();
  }, [pagination.page_size, pagination.page, order, filters]); // Added filters to dependency array

  useEffect(() => {
    try {
      if (searchBar.trim() === "" && searchBar === "") {
        // Code to fetch all when searchengine is empty
        fetchAll();
      } else if (searchBar.trim() !== "") {
        fetchAll();
      }
    } catch (error) {
      if (error.response.status === 401) {
        cookies.remove("token", { path: "/" });
        navigate("/");
      }
    }
  }, [searchBar]);


  const handleSearchChange = useCallback((value) => {
    setSearchBar(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);


  const columns = useMemo(
    () => {
      let columnsArray = [
        {
          Header: (
            <CheckboxHeader currentItem={brands} selectedIds={selectedIds} setSelectedIds={setSelectedIds} pk={'brand_id'}>
            </CheckboxHeader>
          ),
          accessor: "select",
          Cell: ({ row }) => {
            return <CheckboxCell row={row} selectedIds={selectedIds} pk={'brand_id'} handleSelection={handleSelection} />
          }
        },
        {
          Header: () => (
            <SortableHeader currentItem={order} setItem={setOrder} field="name" pos={'start'}>
              Merk naam
            </SortableHeader>
          ),
          accessor: "name",
          Cell: ({ row }) => {
            return <div className="text-start FlatTextLink " style={{ cursor: "pointer" }} onClick={() => { setShowBrandEdit(true); setBrandEditData(row.original); }}>{row.original.name}</div>;
          }
        },
        {
          Header: () => (
            <SortableHeader currentItem={order} setItem={setOrder} field="description" pos={'start'}>
              Omschrijving
            </SortableHeader>
          ),
          accessor: "description",
          Cell: ({ row }) => {
            return <div className="text-start BlackText"
              style={{
                maxWidth: '1000px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >{row.original.description}</div>;
          }
        },
        {
          Header: () => (
            <SortableHeader
              currentItem={order}
              setItem={setOrder}
              field={"status"}
              pos={"end"}
            >
              Status
            </SortableHeader>
          ),
          accessor: "status",
          Cell: ({ row }) => {
            return <div className="text-end" style={{ color: row.original.status == "1" ? "#A9D974" : "#ffcc9d" }}>
              {row.original.status == "1" ? "Gepubliceerd" : "Gearchiveerd"}
            </div>
          }
        },
      ];
      return columnsArray;
    },
    [order.orderBy, order.direction, selectedIds, setSelectedIds, brands]
  );

  //   Table instance
  const tableInstance = useTable({ columns, data: brands });

  const handleSelection = (sizeId, isSelected) => {
    if (isSelected) {
      setSelectedIds([...selectedIds, sizeId]);
    } else {
      setSelectedIds(selectedIds.filter(id => id !== sizeId));
    }
  };

  const deactivateBrands = async () => {
    try {
      const promises = selectedIds.map(id =>
        middleware.put(`products/brands`, {
          brand_id: id,
          status: 0,
        }));
      await Promise.all(promises);
      setSelectedIds([]);
      // Optionally, you can refresh the data or perform other actions here
      fetchAll();
    } catch (error) {
      console.error("Error deactivating sizes:", error);
    }
  };

  const ActivateBrands = async () => {
    try {
      const promises = selectedIds.map(id =>
        middleware.put(`products/brands`, {
          brand_id: id,
          status: 1,
        }));
      await Promise.all(promises);
      setSelectedIds([]);
      fetchAll();
    } catch (error) {
      console.error("Error deactivating sizes:", error);
    }
  };

  const DeleteBrands = async () => {
    await ToastPromise({
      promise: (async () => {
        // Execute delete request
        const response = await middleware.delete(`products/brands?brand_ids=${selectedIds.join(',')}`);
        
        // Check if any brands were actually deleted
        if (response.data?.summary) {
          const { total, deleted, errors } = response.data.summary;
          
          // Handle different scenarios
          if (deleted === 0 && errors > 0) {
            // Nothing deleted, all errors
            throw new Error('no_deletions');
          }
          
          if (deleted > 0) {
            // At least some were deleted, refresh the list
            fetchAll();
            
            // Update selection to only keep failed IDs
            if (errors > 0) {
              const failedIds = response.data.results
                .filter(r => r.status === 'error')
                .map(r => r.brand_id);
            }
          }
        }
        
        setSelectedIds([]);

        return response;
      })(),
      pending: 'Merken worden verwijderd...',
      successRenderer: (response) => {
        const data = response?.data;
        
        if (!data?.summary) {
          return 'Merken verwijderd';
        }
        
        const { total, deleted, errors } = data.summary;
        
        // Build success message based on results
        if (deleted === total) {
          // All successfully deleted
          return deleted === 1 
            ? 'Merk succesvol verwijderd' 
            : `Alle ${deleted} merken succesvol verwijderd`;
        }
        
        if (deleted > 0 && errors > 0) {
          // Partial success
          const inUseCount = data.results.filter(r => 
            r.error?.includes('in use')
          ).length;
          
          if (inUseCount === errors) {
            return `${deleted} van ${total} merken verwijderd. ${errors} ${errors === 1 ? 'merk is' : 'merken zijn'} nog in gebruik.`;
          }
          
          return `${deleted} van ${total} merken verwijderd. ${errors} ${errors === 1 ? 'fout' : 'fouten'} opgetreden.`;
        }
        
        // Should not reach here if throw worked correctly
        return 'Actie voltooid';
      },
      errorRenderer: (error) => {
        console.error("Error deleting brands:", error);
        
        // Check if it's our custom error for no deletions
        if (error?.message === 'no_deletions') {
          const response = error.response || {};
          const data = response.data || {};
          
          if (data.summary?.errors > 0) {
            const inUseCount = data.results?.filter(r => 
              r.error?.includes('in use')
            ).length || 0;
            
            if (inUseCount === data.summary.errors) {
              return data.summary.errors === 1
                ? 'Dit merk kan niet worden verwijderd omdat het nog aan een of meer producten is gekoppeld.'
                : `Deze ${data.summary.errors} merken kunnen niet worden verwijderd omdat ze nog aan een of meer producten zijn gekoppeld.`;
            }
          }
          // Altijd een duidelijke melding geven als fallback
          return 'De geselecteerde merken kunnen niet worden verwijderd omdat ze nog aan een of meer producten zijn gekoppeld.';
        }
        
        // Handle HTTP error codes
        if (error?.response?.status === 403) {
          return 'Je hebt geen toestemming om deze merken te verwijderen';
        }
        
        if (error?.response?.status === 404) {
          return 'Een of meer merken niet gevonden';
        }
        
        return 'Fout bij het verwijderen van merken. Probeer het opnieuw.';
      },
      autoClose: 4000
    });
  };


  function setLoadingScreenFunc() {
    setTimeout(() => {
      setLoading(false);
    }, 400);
  }

  return (
    <>
      <BrandModals brandEdit={showBrandEdit} setBrandEdit={(e) => setShowBrandEdit(e)} brandEditData={brandEditData} brandNew={showBrandNew} setBrandNew={(e) => setShowBrandNew(e)} AddedOrEditedBrand={() => fetchAll()} />
      <ConfirmationModal showConfirmaton={show} setShowConfirmation={(e) => setShow(e)} selectedIds={selectedIds} continueAction={() => deactivateBrands()} />
      <PageTemplate navbarTitle={"Merken Overzicht"}>
        <div className="w-100">
          <div>
            <BackButton
              text={'Terug'}
              onClickFunction={() => navigate('/algemene/instellingen/product')}
            />
          </div>
          <div className="brandDrag">
            {/* <TableDrag
              dataParameter={brands}
              tableLoading={loading}
              pagination={pagination}
              handlePaginationChange={(event) => setPagination(event)}
              primaryKey={"primary_key"}
              urlEndPoint={"products/brands"}
              newItemTitle={"merk"}
              openModal={(e) => openBrandModal(e)}
              dataEditModalSize={() => {}}
              searchTerm={(e) => searchDelay(e)}
              columns={[
                { key: "sorting_order", title: "Sorteer volgorde" },
                { key: "name", title: "Naam" },
                { key: "description", title: "Beschrijving", maxLength: 60 },
              ]}
            /> */}
            <TablePreset
              data={[{ 'selectedIds': selectedIds, 'tableInstance': tableInstance, 'pagination': pagination, 'primary_key': 'brand_id' }]}
              tableLoading={loading}
              actionOptions={[{ title: "Archiveren", function: () => setShow(true) }, { title: "Publiceren", function: () => ActivateBrands() }, { title: "Verwijderen", function: () => DeleteBrands() }]}
              searchBar={[{ placeholder: 'Zoek op merknaam of trefwoorden...', shown: true, searchFunction: handleSearchChange, }]}
              handleSelection={handleSelection}
              setPagination={setPagination}
              // rightDropdowns={[{
              //     placeholder: 'Filter op categorie',
              //     options: [{title: "Optie1"}, {title: "Optie2"}]
              // }]}
              leftButtons={[
                { title: "Filteren", function: () => setShowFilterModal(true) }
              ]}
              rightButtons={[{ title: 'Nieuwe merk', function: () => setShowBrandNew(true) }]}
            />
          </div>
        </div>
        <Filters
          filters={tempfilters}
          showFilterModal={showFilterModal}
          closeFilterModal={CloseFilterModal}
          setTempFilters={setTempFilters}
          setFilters={setFilters}
          defaultFilters={defaultFilters}
        />
      </PageTemplate>
    </>
  );
};

export default BrandSettings;